import moment from 'moment-timezone';
import { getDisplayTimezone } from './localTime';
import { HUB_CUP_ASSETS } from './hubCupAssets';
import { clubLogoForTeam } from './hubClubLogos';

export const HUB_SITE_URL =
  process.env.REACT_APP_HUB_URL || 'https://tol-hub.app';

const HUB_SUPABASE_CANDIDATES = [
  process.env.REACT_APP_HUB_SUPABASE_URL,
  'https://supabase.tol-hub.app',
  'https://tol-hub.app/api/hub-db',
  'https://jtciecuwkhbvnvfpymwi.supabase.co',
]
  .filter(Boolean)
  .map((url) => String(url).replace(/\/$/, ''));

/** Public anon key (same as the Hub client). Override with REACT_APP_HUB_SUPABASE_ANON_KEY. */
const HUB_ANON_KEY =
  process.env.REACT_APP_HUB_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp0Y2llY3V3a2hidm52ZnB5bXdpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc5OTI0MjYsImV4cCI6MjA5MzU2ODQyNn0.MJjSFHrSuS_Bv2Q6gevw7BBQB82btfiU-FTpGkjHC5I';

const TOURNAMENT_NAMES = {
  champions: 'Champions Cup',
  challenge: 'Challenge League',
  conference: 'Conference League',
  asian: 'Asian Champions League',
  european: 'European Champions League',
  libertadores: 'Libertadores',
  clubworld: 'Club World Cup',
  supercup: 'Supercup',
};

const TRIPLET_KEYS = ['champions', 'challenge', 'conference'];

const INTAKE_WINDOWS = [
  {
    key: 'auto_triplet',
    label: 'Champions / Challenge / Conference',
    href: '/tournaments/apply',
    logoKeys: ['champions', 'challenge', 'conference'],
  },
  {
    key: 'asian',
    label: 'Asian Champions League',
    href: '/tournaments/apply/asian',
    logoKeys: ['asian'],
  },
  {
    key: 'european',
    label: 'European Champions League',
    href: '/tournaments/apply/european',
    logoKeys: ['european'],
  },
  {
    key: 'libertadores',
    label: 'Libertadores',
    href: '/tournaments/apply/libertadores',
    logoKeys: ['libertadores'],
  },
  {
    key: 'clubworld',
    label: 'Club World Cup',
    href: '/tournaments/apply/clubworld',
    logoKeys: ['clubworld'],
  },
];

export const HUB_QUICK_LINKS = [
  {
    href: '/tournaments',
    label: 'All competitions',
    hint: 'Live brackets',
    icon: 'cups',
  },
  {
    href: '/tournaments/applications',
    label: 'Applications',
    hint: 'Apply for a cup',
    icon: 'apply',
  },
  {
    href: '/ranking',
    label: 'Ranking',
    hint: 'Cross-server ranking',
    icon: 'ranking',
  },
  { href: '/archive', label: 'Archive', hint: 'Past seasons', icon: 'archive' },
  {
    href: '/champions-cup/tips',
    label: 'Champions Cup tips',
    hint: 'Prediction game',
    icon: 'tips',
  },
  {
    href: '/leagues',
    label: 'League Center',
    hint: 'Every server',
    icon: 'leagues',
  },
  {
    href: '/tournaments/status',
    label: 'Track application',
    hint: 'Look up a tracking ID',
    icon: 'track',
  },
];

export const hubUrl = (path = '/') =>
  `${HUB_SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

export function hubAssetUrl(path) {
  if (!path) return null;
  const raw = String(path).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${HUB_SITE_URL}/${raw
    .replace(/^\//, '')
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')}`;
}

const INTAKE_SERVER_TIMEZONES = [
  'Africa/Dar_es_Salaam',
  'America/Los_Angeles',
  'America/New_York',
  'America/Sao_Paulo',
  'America/Tijuana',
  'Asia/Dubai',
  'Asia/Manila',
  'Asia/Qatar',
  'Asia/Seoul',
  'Asia/Singapore',
  'Atlantic/Reykjavik',
  'Australia/Melbourne',
  'Europe/Berlin',
  'Europe/Bucharest',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/London',
  'Europe/Monaco',
  'Europe/Sofia',
  'Europe/Warsaw',
];

const INTAKE_LOCAL_HOUR = 21;

function parseYmdParts(ymd) {
  const parts = String(ymd || '')
    .trim()
    .split('-')
    .map((part) => Number(part));
  return {
    year: parts[0] || 1970,
    month: parts[1] || 1,
    day: parts[2] || 1,
  };
}

function addCalendarDays(ymd, days) {
  const base = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day + days));
  return {
    year: base.getUTCFullYear(),
    month: base.getUTCMonth() + 1,
    day: base.getUTCDate(),
  };
}

function partsInTimezone(date, timezone) {
  try {
    const map = {};
    new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(date)
      .forEach((part) => {
        if (part.type !== 'literal') map[part.type] = part.value;
      });
    return {
      year: Number(map.year || 0),
      month: Number(map.month || 0),
      day: Number(map.day || 0),
      hour: Number(map.hour || 0),
      minute: Number(map.minute || 0),
    };
  } catch (error) {
    return null;
  }
}

function localStamp(parts) {
  return (
    (((parts.year * 12 + parts.month) * 31 + parts.day) * 24 + parts.hour) *
      60 +
    parts.minute
  );
}

function serverLocalDateTimeToUtc(ymd, hour, minute, timezone) {
  let guess = new Date(
    Date.UTC(ymd.year, ymd.month - 1, ymd.day, hour, minute, 0)
  );
  const desired = { ...ymd, hour, minute };
  for (let index = 0; index < 4; index += 1) {
    const current = partsInTimezone(guess, timezone);
    if (!current) return null;
    const diffMinutes = localStamp(desired) - localStamp(current);
    if (diffMinutes === 0) break;
    guess = new Date(guess.getTime() + diffMinutes * 60_000);
  }
  return guess;
}

function computeServerLocalIntakeBounds(fridayYmd) {
  const thursday = addCalendarDays(fridayYmd, -1);
  const monday = addCalendarDays(fridayYmd, 3);
  let openAt = null;
  let closeAt = null;
  INTAKE_SERVER_TIMEZONES.forEach((timezone) => {
    const open = serverLocalDateTimeToUtc(
      thursday,
      INTAKE_LOCAL_HOUR,
      0,
      timezone
    );
    const close = serverLocalDateTimeToUtc(
      monday,
      INTAKE_LOCAL_HOUR,
      0,
      timezone
    );
    if (open && (!openAt || open.getTime() > openAt.getTime())) openAt = open;
    if (close && (!closeAt || close.getTime() > closeAt.getTime())) {
      closeAt = close;
    }
  });
  if (!openAt || !closeAt || openAt.getTime() >= closeAt.getTime()) return null;
  return { start: openAt, end: closeAt };
}

function getRecurrenceIntakeWindow(config, now = new Date()) {
  const intervalWeeks = Math.max(1, Number(config?.interval_weeks) || 1);
  const anchor = parseYmdParts(config?.recurrence_anchor_friday);
  const first = computeServerLocalIntakeBounds(anchor);
  if (!first) return null;
  if (now.getTime() < first.start.getTime()) return first;

  const approxMs = intervalWeeks * 7 * 86_400_000;
  let step = Math.max(
    0,
    Math.floor((now.getTime() - first.start.getTime()) / approxMs) - 1
  );
  for (let guard = 0; guard < 400; guard += 1) {
    const friday = addCalendarDays(anchor, step * intervalWeeks * 7);
    const bounds = computeServerLocalIntakeBounds(friday);
    if (!bounds) return null;
    if (now.getTime() < bounds.end.getTime()) return bounds;
    step += 1;
  }
  return null;
}

function parseSeasonNumber(label) {
  const match = String(label || '').trim().match(/(\d+)/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

export function formatHubInstant(value, zone = getDisplayTimezone()) {
  if (!value) return null;
  const local = moment.utc(value).tz(zone || 'UTC');
  return local.isValid() ? local.format('ddd, D MMM, HH:mm z') : null;
}

async function hubSelect(table, query) {
  let lastError = null;
  for (let index = 0; index < HUB_SUPABASE_CANDIDATES.length; index += 1) {
    const base = HUB_SUPABASE_CANDIDATES[index];
    try {
      const response = await fetch(`${base}/rest/v1/${table}?${query}`, {
        headers: {
          apikey: HUB_ANON_KEY,
          Authorization: `Bearer ${HUB_ANON_KEY}`,
        },
      });
      if (!response.ok) {
        lastError = new Error(`Hub ${table} ${response.status}`);
        continue;
      }
      return response.json();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error(`Hub ${table} failed`);
}

function catalogVisuals(row, tournamentKey) {
  const local = HUB_CUP_ASSETS[tournamentKey || row?.tournament_key] || null;
  return {
    logoLight:
      local?.logoLight || hubAssetUrl(row?.list_logo_url || row?.logo_url),
    logoDark: local?.logoDark || hubAssetUrl(row?.logo_url || row?.list_logo_url),
  };
}

function competitionName(tournamentKey, catalogByKey) {
  const row = catalogByKey.get(tournamentKey);
  return row?.display_name || TOURNAMENT_NAMES[tournamentKey] || tournamentKey;
}

function visualsFor(tournamentKey, catalogByKey) {
  return catalogVisuals(catalogByKey.get(tournamentKey), tournamentKey);
}

function lastSeasonWinners(catalogByKey, catalogOrder, archives, results) {
  if (!Array.isArray(archives) || !Array.isArray(results)) return null;
  const latest = new Map();
  archives
    .filter((row) => row && row.tournament_key)
    .forEach((row) => {
      const season = parseSeasonNumber(row.season_label);
      if (season === null) return;
      const previous = latest.get(row.tournament_key);
      const previousSeason = previous
        ? parseSeasonNumber(previous.season_label)
        : null;
      const previousTime = previous?.created_at
        ? new Date(previous.created_at).getTime()
        : 0;
      const nextTime = row.created_at ? new Date(row.created_at).getTime() : 0;
      if (
        !previous ||
        season > previousSeason ||
        (season === previousSeason && nextTime > previousTime)
      ) {
        latest.set(row.tournament_key, row);
      }
    });

  const items = [];
  latest.forEach((season, key) => {
    const seasonNumber = parseSeasonNumber(season.season_label);
    const winner = results.find(
      (row) =>
        row &&
        row.tournament_key === key &&
        parseSeasonNumber(row.season_label) === seasonNumber &&
        String(row.placement || '').toLowerCase() === 'winner'
    );
    if (!winner) return;
    items.push({
      key,
      name: competitionName(key, catalogByKey),
      seasonLabel: seasonNumber !== null ? `S ${seasonNumber}` : season.season_label,
      href: season.id
        ? hubUrl(`/archive/${key}/${season.id}`)
        : hubUrl('/archive'),
      ...visualsFor(key, catalogByKey),
      team: winner.team || '',
      teamLogo: clubLogoForTeam(winner.team),
      username: winner.username || '',
      server: winner.server || '',
    });
  });

  return items.sort((a, b) => {
    const orderA = catalogOrder.has(a.key) ? catalogOrder.get(a.key) : 999;
    const orderB = catalogOrder.has(b.key) ? catalogOrder.get(b.key) : 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
}

function sharedSeasonLabel(tournaments) {
  const tripletSeasons = tournaments
    .filter(
      (row) =>
        row &&
        row.season_type === 'current' &&
        row.is_active &&
        TRIPLET_KEYS.includes(row.tournament_key)
    )
    .map((row) => parseSeasonNumber(row.season_label))
    .filter((value) => value !== null);
  if (!tripletSeasons.length) return null;
  return `Season ${Math.max(...tripletSeasons)}`;
}

function autoWindowStatus(config, now) {
  if (!config?.recurrence_anchor_friday) {
    return { open: false, opensAt: null, closesAt: null };
  }
  const window = getRecurrenceIntakeWindow(config, now);
  if (!window) {
    return { open: false, opensAt: null, closesAt: null };
  }
  const open =
    now.getTime() >= window.start.getTime() &&
    now.getTime() < window.end.getTime();
  return {
    open,
    opensAt: window.start.toISOString(),
    closesAt: window.end.toISOString(),
  };
}

export async function fetchCrossServerSummary() {
  const [
    tournamentsResult,
    catalogResult,
    windowsResult,
    configResult,
    archiveResult,
    winnersResult,
  ] = await Promise.allSettled([
      hubSelect(
        'tournaments',
        'select=tournament_key,season_label,is_active,season_type&season_type=eq.current&is_active=eq.true'
      ),
      hubSelect(
        'tournament_catalog',
        'select=tournament_key,display_name,sort_order,logo_url,list_logo_url,cup_asset_url&order=sort_order'
      ),
      hubSelect(
        'application_intake_windows',
        'select=application_key,manual_mode'
      ),
      hubSelect(
        'application_intake_config',
        'select=id,manual_mode,recurrence_anchor_friday,interval_weeks'
      ),
      hubSelect(
        'tournaments',
        'select=id,tournament_key,season_label,created_at&season_type=eq.archive&order=created_at.desc'
      ),
      hubSelect(
        'tournament_results',
        'select=tournament_key,season_label,username,team,server,placement&placement=eq.Winner'
      ),
    ]);

  const catalogRows =
    catalogResult.status === 'fulfilled' && Array.isArray(catalogResult.value)
      ? catalogResult.value
      : [];
  const catalogByKey = new Map(
    catalogRows
      .filter((row) => row && row.tournament_key)
      .map((row) => [row.tournament_key, row])
  );
  const catalogOrder = new Map(
    catalogRows
      .filter((row) => row && row.tournament_key)
      .map((row, index) => [
        row.tournament_key,
        Number.isFinite(row.sort_order) ? row.sort_order : index,
      ])
  );

  let competitions = null;
  let seasonLabel = null;
  if (
    tournamentsResult.status === 'fulfilled' &&
    Array.isArray(tournamentsResult.value)
  ) {
    seasonLabel = sharedSeasonLabel(tournamentsResult.value);
    competitions = tournamentsResult.value
      .filter((row) => row && row.tournament_key)
      .map((row) => ({
        key: row.tournament_key,
        name: competitionName(row.tournament_key, catalogByKey),
        href: hubUrl(`/current/${row.tournament_key}`),
        ...visualsFor(row.tournament_key, catalogByKey),
      }))
      .sort((a, b) => {
        const orderA = catalogOrder.has(a.key) ? catalogOrder.get(a.key) : 999;
        const orderB = catalogOrder.has(b.key) ? catalogOrder.get(b.key) : 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
      });
  }

  const intakeConfig =
    configResult.status === 'fulfilled' && Array.isArray(configResult.value)
      ? configResult.value[0] || null
      : null;
  const now = new Date();
  const autoStatus = autoWindowStatus(intakeConfig, now);

  let applications = null;
  if (
    windowsResult.status === 'fulfilled' &&
    Array.isArray(windowsResult.value)
  ) {
    const byKey = new Map(
      windowsResult.value
        .filter((row) => row && row.application_key)
        .map((row) => [row.application_key, row.manual_mode])
    );
    applications = INTAKE_WINDOWS.filter((item) => byKey.has(item.key)).map(
      (item) => {
        const mode = byKey.get(item.key);
        const isAuto = item.key === 'auto_triplet' && mode === 'auto';
        const open =
          mode === 'force_open' ? true : mode === 'force_closed' ? false : autoStatus.open;
        return {
          key: item.key,
          name: item.label,
          href: hubUrl(item.href),
          open,
          opensAt: isAuto && !open ? autoStatus.opensAt : null,
          closesAt: isAuto && open ? autoStatus.closesAt : null,
          logos: (item.logoKeys || [])
            .map((key) => ({ key, ...visualsFor(key, catalogByKey) }))
            .filter((logo) => logo.logoLight || logo.logoDark),
        };
      }
    );
  }

  const winners = lastSeasonWinners(
    catalogByKey,
    catalogOrder,
    archiveResult.status === 'fulfilled' ? archiveResult.value : null,
    winnersResult.status === 'fulfilled' ? winnersResult.value : null
  );

  return {
    seasonLabel,
    competitions,
    applications,
    winners,
    competitionsFailed: competitions === null,
    applicationsFailed: applications === null,
    winnersFailed: winners === null,
  };
}
